import React, { useState, useEffect } from "react";
import {
	Box,
	Text,
	VStack,
	HStack,
	Image,
	Input,
	Button,
	Grid,
	GridItem,
	useToast,
} from "@chakra-ui/react";
import { WebUrl } from '../../routes';
import Routes from '../../common/api_routes';
import { _CRYPTOCURRENCIES } from "../../common/constant";
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { stringIsNullOrEmpty } from "common/util";
import { useDispatch } from "react-redux";
import { callback } from "../../redux/transaction/action";
import ApiEngine from "../../common/api-engine";

const shortcutAmounts = [50, 100, 200, 500];

/// <summary>
/// Author: CK
/// </summary>
const CryptoDepositPage = () => {
	const _dispatch = useDispatch();
	const [selectedCrypto, setSelectedCrypto] = useState(_CRYPTOCURRENCIES[0]);
	const [amount, setAmount] = useState("");
	const [loading, setLoading] = useState(false);
	const toast = useToast();
	const navigate = useNavigate();
	var _location = useLocation();
	const _queryParams = new URLSearchParams(_location.search);
	const _merchantCode = _queryParams.get("merchantCode");

	/// <summary>
	/// Author: CK
	/// </summary>
	useEffect(() => {
		if (stringIsNullOrEmpty(_merchantCode)) {
			_dispatch(callback("Invalid merchant code."));
			return;
		}
	}, []);

	/// <summary>
	/// Author: CK
	/// </summary>
	const handleSelectCrypto = (crypto) => {
		setSelectedCrypto(crypto);
		toast({
			title: `${crypto.name} selected.`,
			status: "info",
			duration: 2000,
			isClosable: true,
		});
	};

	/// <summary>
	/// Author: CK
	/// </summary>
	const handleShortcutClick = (value) => {
		setAmount(value.toString());
	};

	/// <summary>
	/// Author: CK
	/// </summary>
	const handleSubmit = async () => {
		const numericAmount = parseFloat(amount);

		if (!selectedCrypto) {
			toast({
				title: "No cryptocurrency selected!",
				status: "error",
				duration: 3000,
				isClosable: true,
			});
			return;
		}

		if (!amount || isNaN(numericAmount) || numericAmount < 1 || numericAmount > 2500) {
			toast({
				title: "Invalid amount!",
				description: "Amount must be between 1 MYR and 2500 MYR.",
				status: "error",
				duration: 3000,
				isClosable: true,
			});
			return;
		}

		setLoading(true);

		try {
			const result = await ApiEngine.post(Routes._INIT_TRANSACTION, {
				"CryptocurrencyId": selectedCrypto.id,
				"ClientCode": _merchantCode,
				"Amount": numericAmount
			});

			if (result.success) {
				window.location.href = result.data.redirectPage;
				// navigate(WebUrl._TRANSACTION_INIT, { state: result.data.transaction, sessionToken: result.data.sessionToken });
			}
			else {
				if (result.callback) {
					_dispatch(callback(result.message || "Something went wrong."));
					return;
				}

				toast({
					title: "Error",
					description: result.message || "Something went wrong.",
					status: "error",
					duration: 3000,
					isClosable: true,
				});
			}
		} catch (error) {
			toast({
				title: "Error",
				description: "Failed to initiate the transaction.",
				status: "error",
				duration: 3000,
				isClosable: true,
			});
		}
		finally {
			setLoading(false);
		}

	};

	return (
		<Box maxW="500px" mx="auto" p={6}>
			<Text fontSize="2xl" fontWeight="bold" mb={4}>
				Deposit Cryptocurrency
			</Text>

			{/* Cryptocurrency Selection */}
			<Text mb={2}>Select Cryptocurrency:</Text>
			<Grid templateColumns="repeat(3, 1fr)" gap={4}>
				{_CRYPTOCURRENCIES.map((crypto) => (
					<GridItem key={crypto.symbol}>
						<Box
							borderWidth={2}
							borderRadius="md"
							borderColor={selectedCrypto?.symbol === crypto.symbol ? "blue.500" : "gray.200"}
							p={2}
							cursor="pointer"
							onClick={() => handleSelectCrypto(crypto)}
							_hover={{ borderColor: "blue.300" }}
						>
							<Image src={crypto.image} alt={crypto.name} boxSize="50px" mx="auto" />
							<Text textAlign="center" mt={2} fontSize="sm" fontWeight="medium">
								{crypto.name}
							</Text>
						</Box>
					</GridItem>
				))}
			</Grid>

			{/* Amount Input */}
			<VStack align="start" mt={6} spacing={4}>
				<Box w="100%">
					<Text mb={1}>Enter Amount:</Text>
					<Input
						placeholder="Enter amount to deposit"
						type="number"
						min={1}
						max={2500}
						value={amount}
						onChange={(e) => {
							const value = e.target.value;
							if (value === "" || (parseFloat(value) >= 1 && parseFloat(value) <= 2500)) {
								setAmount(value);
							}
						}}
					/>
					<Text fontSize="sm" color="gray.500" mt={1}>
						Minimum amount: 1 MYR, Maximum amount: 2500 MYR
					</Text>
				</Box>

				{/* Shortcut Buttons */}
				<HStack spacing={4} w="100%">
					{shortcutAmounts.map((amt) => (
						<Button
							key={amt}
							onClick={() => handleShortcutClick(amt)}
							colorScheme="blue"
							variant="outline"
							w="100px" // Ensures uniform button width
						>
							{amt}
						</Button>
					))}
				</HStack>
			</VStack>

			{/* Submit Button */}
			<Button
				colorScheme="green"
				mt={6}
				w="100%"
				onClick={handleSubmit}
				isDisabled={!selectedCrypto || !amount}
				isLoading={loading}
			>
				Submit Deposit
			</Button>
		</Box>
	);
};

export default CryptoDepositPage;
